import { FC, useCallback, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import Button from '@/storybook/components/Button/Button';
import DownloadButton from '@/storybook/components/DownloadButton/DownloadButton';
import { useToast } from '@/storybook/components/Toast/Toast';
import downloadFile from '@/storybook/utils/downloadFile';
import { BYTES_PER_KILOBYTE } from '../ChatMessage/ChatMessage.utils';
import styles from './ChatImagePreview.module.css';
import { CHAT_IMAGE_PREVIEW_TEST_IDS } from './testIds';

const DOWNLOAD_TOOLTIP_ID = 'chatbot-image-preview-download-tooltip';

export interface ChatImagePreviewProps {
  url: string;
  filename: string;
  sizeBytes: number;
}

type ImageStatus = 'loading' | 'loaded' | 'error';

const ChatImagePreview: FC<ChatImagePreviewProps> = ({ url, filename, sizeBytes }) => {
  const { t } = useTranslation();
  const { makeToast } = useToast();
  const [status, setStatus] = useState<ImageStatus>('loading');
  const [reloadKey, setReloadKey] = useState(0);

  const handleLoad = useCallback(() => {
    setStatus('loaded');
  }, []);

  const handleError = useCallback(() => {
    setStatus('error');
  }, []);

  const handleRetry = useCallback(() => {
    setStatus('loading');
    setReloadKey(key => key + 1);
  }, []);

  const handleDownload = useCallback(async () => {
    downloadFile(url, filename);
    makeToast({ content: String(t('main.download.success')), appearance: 'success' });
  }, [filename, makeToast, t, url]);

  const sizeInKilobytes = Math.round(sizeBytes / BYTES_PER_KILOBYTE);
  const downloadAriaLabel = t('components.aiChatDrawer.imagePreview.downloadAriaLabel', {
    filename,
    size: sizeInKilobytes,
  });

  return (
    <figure className={styles.container} data-testid={CHAT_IMAGE_PREVIEW_TEST_IDS.container}>
      <div
        className={styles.downloadButtonWrapper}
        data-tooltip-id={DOWNLOAD_TOOLTIP_ID}
        data-tooltip-content={downloadAriaLabel}
      >
        <DownloadButton
          onDownload={handleDownload}
          className={styles.downloadButton}
          aria-label={downloadAriaLabel}
          data-testid={CHAT_IMAGE_PREVIEW_TEST_IDS.downloadLink}
        />
      </div>
      <Tooltip id={DOWNLOAD_TOOLTIP_ID} place="top" />
      {status === 'error' ? (
        <div className={styles.errorState} data-testid={CHAT_IMAGE_PREVIEW_TEST_IDS.error} role="alert">
          <span className={styles.errorMessage}>{t('components.aiChatDrawer.imagePreview.errorMessage')}</span>
          <Button
            variant="text"
            size="S"
            className={styles.retryButton}
            onClick={handleRetry}
            data-testid={CHAT_IMAGE_PREVIEW_TEST_IDS.retryButton}
          >
            {t('components.aiChatDrawer.imagePreview.retry')}
          </Button>
        </div>
      ) : (
        <>
          {status === 'loading' ? (
            <div className={styles.skeleton} data-testid={CHAT_IMAGE_PREVIEW_TEST_IDS.skeleton} aria-hidden="true" />
          ) : null}
          <img
            key={reloadKey}
            src={url}
            alt={t('components.aiChatDrawer.imagePreview.imageAlt', { filename })}
            className={status === 'loaded' ? styles.image : styles.imageHidden}
            onLoad={handleLoad}
            onError={handleError}
            data-testid={CHAT_IMAGE_PREVIEW_TEST_IDS.image}
          />
        </>
      )}
    </figure>
  );
};

export default ChatImagePreview;
